"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { CalendarX, CircleCheck, Home, MapPin } from "lucide-react";
import type { FormState } from "@/modules/shared/forms";
import {
  serviceLocationLabel,
  type ServiceLocation,
} from "@/modules/shared/serviceLocation";
import { CardMapLink } from "@/design/CardMapLink";
import { initialBookingForm } from "./bookingForm";
import { buttonClass } from "./buttons";
import { serviceRowState, type ServiceRowState } from "./serviceRow";
import { Banner } from "./ui";

/**
 * Consumer booking entry on the public profiles
 * (docs/ux/screens/BusinessPublicProfileScreen.md,
 * PractitionerPublicProfileScreen.md, ClinicAnyPractitionerScreen.md; flow
 * docs/ux/flows/consumer-booking.md). Pick a Service, where to have it, and a
 * Time Block, review it, then send → `BookingRequested`. Only a logged-in
 * Consumer may book; everyone else sees availability read-only.
 *
 * Three things changed when the demo's booking screen was ported in, and each
 * one is here for a reason worth keeping:
 *
 * 1. **Time is chosen day-first.** The old version printed every open block of
 *    every open day as one wall of identical buttons. It was accurate and
 *    unusable: nothing said which days were busy, and every button looked like
 *    every other. Now the days are cards that say how many times are free, and
 *    only the chosen day opens, split into morning, afternoon and evening,
 *    which is how people describe when they can come.
 * 2. **Where you have it is a card, and each card states its own price.** Not a
 *    surcharge bolted onto a number somewhere else: the price of having it that
 *    way is the whole answer on the card.
 * 3. **Nothing is sent by touching a time.** Every time chip used to be its own
 *    submit, so a mistap sent a real request to a real business with no way
 *    back. Choosing is now separate from sending, and what will be sent is
 *    written out in full above the button that sends it.
 */

/**
 * What the host is asked to send. Named and typed here rather than assembled
 * as FormData, because this is the shape both hosts have to agree on and a
 * bag of strings agrees on nothing.
 */
export interface BookingRequestInput {
  businessId: string;
  serviceId: string;
  date: string;
  startMinutes: number;
  endMinutes: number;
  serviceLocation: ServiceLocation;
  /** Required for an At-Home request and absent otherwise (H24). */
  homeAddress?: string;
}

/**
 * A link that uses the host's router when there is one and is an ordinary
 * anchor when there is not. Keeping it an `<a>` either way is the point: the
 * middle-click, the "open in new tab" and the screen-reader announcement are
 * all things a button would have thrown away.
 */
function AppLink({
  href,
  onNavigate,
  className = "font-semibold underline",
  children,
}: {
  href: string;
  onNavigate?: (href: string) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        if (!onNavigate || event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        onNavigate(href);
      }}
    >
      {children}
    </a>
  );
}

export interface BookServiceView {
  id: string;
  name: string;
  /** The base price (In-Clinic), used where a location is not in play. */
  priceDisplay: string;
  durationDisplay: string;
  /**
   * The price of having this Service in each place it is offered, already
   * totalled. In-Clinic is the base; At-Home is the base plus the business's
   * home-visit surcharge (H24), added here so the reader never has to.
   *
   * When per-location pricing lands (H30) the business sets these directly and
   * the addition goes away; the shape this component reads does not change.
   */
  priceByLocation: Partial<Record<ServiceLocation, string>>;
}

export interface BookBlockView {
  startMinutes: number;
  endMinutes: number;
  /** The whole block, e.g. "09:00–09:30". Shown in the summary. */
  timeLabel: string;
  /** Just the start, e.g. "09:00". Shown on the chip, where width is scarce. */
  startLabel: string;
}

export interface BookDayView {
  date: string;
  dateLabel: string;
  /** Weekday initials for the day card, e.g. "Mon". */
  weekdayLabel: string;
  /** Day of the month for the day card, e.g. "4". */
  dayOfMonth: string;
  blocks: BookBlockView[];
}

/** Before noon, before five, and after it. Enough to break a long list up. */
type PartOfDay = "Morning" | "Afternoon" | "Evening";
const PARTS: PartOfDay[] = ["Morning", "Afternoon", "Evening"];

function partOfDay(startMinutes: number): PartOfDay {
  if (startMinutes < 12 * 60) return "Morning";
  if (startMinutes < 17 * 60) return "Afternoon";
  return "Evening";
}

/** Days shown before "Show more days"; enough for a fortnight of browsing. */
const DAYS_SHOWN = 8;

/**
 * The clinic booking widget.
 *
 * **One target since H45: the clinic.** There used to be two, a named
 * practitioner and "any practitioner", because a Consumer could pick a person.
 * They cannot, so the union collapsed to the business id and the practitioner
 * summary line went with it. The clinic assigns somebody at approval, and the
 * Consumer learns who then.
 */
export function BookAppointment({
  businessId,
  days,
  daysByLocation,
  services,
  viewer,
  offeredLocations,
  mapHref,
  loginHref,
  bookingsHref = "/bookings",
  onNavigate,
  onRequest,
}: {
  businessId: string;
  /** Default availability (the whole-clinic union); shown to non-consumers. */
  days: BookDayView[];
  /** Per-Service-Location availability (H24): the times a Consumer sees track
   *  the selected location, since only practitioners who offer it contribute
   *  blocks. */
  daysByLocation?: Partial<Record<ServiceLocation, BookDayView[]>>;
  services: BookServiceView[];
  viewer: "consumer" | "guest" | "other";
  /** Service Locations offered here (H24), the clinic-wide union. Non-empty. */
  offeredLocations: ServiceLocation[];
  /** Google Maps link for the clinic, shown on the In-Clinic card. */
  mapHref?: string;
  /**
   * Where a guest goes to log in, already carrying the return path.
   * Computed by the host, because only the host knows what "here" is
   * called in its router.
   */
  loginHref: string;
  /** Where "your bookings" points after a request is sent. */
  bookingsHref?: string;
  /**
   * Client-side navigation, when the host has a router. Omitted, the links
   * are ordinary anchors and the page reloads, which is correct but slower.
   */
  onNavigate?: (href: string) => void;
  /**
   * Send the request. The screen supplies it, because transport is not a rule
   * and this file is not allowed to hold one.
   *
   * **Everything that decides whether the button may be pressed stays
   * here**, because it is H24 and H45 rather than transport: an At-Home
   * request needs an address, a time must be chosen, and no
   * `practitionerId` is ever sent.
   */
  onRequest: (input: BookingRequestInput) => Promise<FormState>;
}) {
  const initial = initialBookingForm(services, offeredLocations);
  const [serviceId, setServiceId] = useState(initial.serviceId);
  const [serviceLocation, setServiceLocation] = useState<ServiceLocation>(
    initial.serviceLocation,
  );
  const [homeAddress, setHomeAddress] = useState(initial.homeAddress);
  const [picked, setPicked] = useState(initial.picked);
  // Bumped when the form clears, so the time picker reopens on the first day
  // with its "Show more days" folded again rather than where it was left.
  const [formRound, setFormRound] = useState(0);
  // What is stopping the request from being sent, said where the person just
  // pressed. An earlier version put it under the times, which is the wrong
  // place for "you have not given an address": the field it is about is a
  // panel further up, and the message sat below the thing they had already
  // done rather than beside the button that refused.
  const [blocked, setBlocked] = useState<string | null>(null);
  const [state, setState] = useState<FormState>({});
  const [sending, setSending] = useState(false);

  const atHome = serviceLocation === "at_home";
  const isConsumer = viewer === "consumer";
  // Clinic-wide on purpose: `days` is the union across every Where, so a
  // location with no times does not dim services the other one can deliver.
  const rowState = serviceRowState(viewer, days);
  const available = rowState !== "unavailable";

  // A Consumer's slots follow the selected location when per-location
  // availability is supplied (Any-practitioner, H24); otherwise the default set.
  const shownDays =
    isConsumer && daysByLocation ? daysByLocation[serviceLocation] ?? [] : days;

  const service = services.find((s) => s.id === serviceId) ?? services[0];
  const pickedDay = shownDays.find((d) => d.date === picked?.date) ?? null;
  const pickedBlock =
    pickedDay?.blocks.find((b) => b.startMinutes === picked?.startMinutes) ?? null;
  // An At-Home request needs an address before it can be sent (H24).
  const addressMissing = atHome && homeAddress.trim() === "";
  const totalDisplay = service?.priceByLocation[serviceLocation] ?? service?.priceDisplay;


  // Changing the service or the location can invalidate what was chosen: a
  // location has its own availability, so the block picked under the other one
  // may not exist here. Dropping it is the honest move; carrying it forward
  // would send a request for a time this location does not actually offer.
  const changeLocation = (next: ServiceLocation) => {
    setServiceLocation(next);
    if (daysByLocation) setPicked(null);
  };

  // A sent request clears everything chosen and typed, back to how the form
  // opened. A refusal does not come here: re-picking a time is the whole
  // recovery from one, and clearing the form would make somebody else taking
  // the slot feel like a mistake of theirs.
  const clearForm = () => {
    const fresh = initialBookingForm(services, offeredLocations);
    setServiceId(fresh.serviceId);
    setServiceLocation(fresh.serviceLocation);
    setHomeAddress(fresh.homeAddress);
    setPicked(fresh.picked);
    setBlocked(null);
    setFormRound((n) => n + 1);
  };

  return (
    <section>
      <h2 className="mt-8 mb-3 text-lg font-bold text-ink">Book an appointment</h2>

      <p className="mb-3 text-sm text-muted">
        Pick a service and a time. The clinic confirms and tells you who you are
        seeing.
      </p>

      {viewer === "guest" && available && (
        <Banner tone="info">
          <AppLink href={loginHref} onNavigate={onNavigate}>
            Log in
          </AppLink>{" "}
          as a customer to book one of these times.
        </Banner>
      )}
      {viewer === "other" && available && (
        <Banner tone="info">
          Booking is for customer accounts. Availability is shown below.
        </Banner>
      )}

      {services.length === 0 ? (
        <p className="text-sm text-muted">
          This business hasn&apos;t listed its services yet.
        </p>
      ) : (
        <>
          {isConsumer && state.formError && <Banner>{state.formError}</Banner>}
          {isConsumer && state.success && (
            <RequestSentDialog
              bookingsHref={bookingsHref}
              onNavigate={onNavigate}
              onClose={() => setState({})}
            />
          )}

          <div className="space-y-4">
            <ServiceList
              services={services}
              rowState={rowState}
              selectedId={serviceId}
              onSelect={setServiceId}
              location={serviceLocation}
            />

            {/* Everything below needs an open time somewhere. Without one the
                list above has already said so, and a Where or a summary for a
                request that cannot be sent is a form that only refuses. */}

            {/* Service Location (H24). Shown whenever a Consumer is booking,
                even with one option: "this is a home visit only" is worth
                saying, and it is where the price for that way is stated. */}
            {available && isConsumer && service && (
              <WhereToHaveIt
                offered={offeredLocations}
                selected={serviceLocation}
                onChange={changeLocation}
                service={service}
                mapHref={mapHref}
              />
            )}

            {available && isConsumer && atHome && (
              <Panel>
                <label htmlFor="homeAddress" className="mb-1 block font-bold text-ink">
                  Your address
                </label>
                <p className="mb-2 text-sm text-muted">
                  Where the practitioner should visit.
                </p>
                <textarea
                  id="homeAddress"
                  value={homeAddress}
                  onChange={(e) => setHomeAddress(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Building, street, area"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-body outline-none focus:border-accent"
                />
              </Panel>
            )}

            {available && shownDays.length === 0 && (
              // Only one Where can get here: the clinic has times somewhere
              // (the list would be dimmed otherwise), just not this way.
              <Panel>
                <h3 className="mb-1 font-bold text-ink">Pick a time</h3>
                <p className="text-sm text-muted">
                  {atHome
                    ? "No open times for at-home visits in the next 7 days."
                    : "No open times in the clinic in the next 7 days."}
                </p>
              </Panel>
            )}

            {available && shownDays.length > 0 && (
              <PickATime
                key={formRound}
                days={shownDays}
                picked={picked}
                onPick={(next) => {
                  setPicked(next);
                  setBlocked(null);
                }}
                readOnly={!isConsumer}
              />
            )}

            {available && isConsumer && service && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!pickedBlock || addressMissing) return;
                  setSending(true);
                  setState({});
                  // The clinic, and only the clinic (H45). There is no
                  // practitionerId to send: a Consumer names a place and a
                  // time, never a person.
                  void onRequest({
                    businessId,
                    serviceId,
                    date: picked?.date ?? "",
                    startMinutes: pickedBlock.startMinutes,
                    endMinutes: pickedBlock.endMinutes,
                    serviceLocation,
                    homeAddress: atHome ? homeAddress : undefined,
                  })
                    .then((result) => {
                      setState(result);
                      if (result.success) clearForm();
                    })
                    .finally(() => setSending(false));
                }}
              >
                <Panel>
                  <h3 className="mb-3 font-bold text-ink">Your request</h3>
                  <dl className="space-y-2 text-sm">
                    <SummaryRow label="Service" value={service.name} />
                    <SummaryRow label="Length" value={service.durationDisplay} />
                    <SummaryRow
                      label="Where"
                      value={serviceLocationLabel(serviceLocation)}
                    />
                    <SummaryRow
                      label="When"
                      value={
                        pickedDay && pickedBlock
                          ? `${pickedDay.dateLabel} · ${pickedBlock.timeLabel}`
                          : "Not chosen yet"
                      }
                    />
                  </dl>
                  <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
                    <span className="font-semibold text-ink">Total</span>
                    <span className="font-mono text-lg font-bold text-ink">
                      {totalDisplay}
                    </span>
                  </div>

                  {/* Said here as well as at the top, because this is the
                      moment somebody commits and the summary has no
                      "who" row to answer the question on its own. */}
                  <p className="mt-3 text-sm text-muted">
                    The clinic tells you who you are seeing when it confirms.
                  </p>

                  {blocked && (
                    <p className="mt-4 text-sm font-medium text-danger">{blocked}</p>
                  )}

                  <SendRequest
                    ready={pickedBlock !== null && !addressMissing}
                    pending={sending}
                    onBlocked={() =>
                      setBlocked(
                        pickedBlock === null
                          ? "Pick a time before sending your request."
                          : "Enter the address where the practitioner should visit.",
                      )
                    }
                  />
                  <p className="mt-3 text-center text-xs text-muted">
                    You are not charged now, and the business confirms before it
                    is booked.
                  </p>
                </Panel>
              </form>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The clinic's services, once. This list used to exist twice: an informational
 * one under the profile header and a `<select>` in here naming the same
 * services again. It is now the selector itself for a Consumer, a plain list
 * for anybody else, and dimmed with a "No open times" tag when nothing can be
 * requested (`serviceRow.ts`).
 *
 * The price shown is for the Where currently selected (H24), so the row, the
 * Where card and the summary's total always state the same number.
 */
function ServiceList({
  services,
  rowState,
  selectedId,
  onSelect,
  location,
}: {
  services: BookServiceView[];
  rowState: ServiceRowState;
  selectedId: string;
  onSelect: (id: string) => void;
  location: ServiceLocation;
}) {
  const selectable = rowState === "selectable";
  const unavailable = rowState === "unavailable";
  const shape = "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-start";

  return (
    <Panel>
      <h3 id="services-heading" className="mb-1 font-bold text-ink">
        Services
      </h3>
      <p className="mb-3 text-sm text-muted">
        {unavailable
          ? "No open times in the next 7 days, so these cannot be booked right now."
          : selectable
            ? "Choose the one you want to book."
            : "One price per service, whoever you see."}
      </p>

      <ul
        role={selectable ? "radiogroup" : undefined}
        aria-labelledby="services-heading"
        className="space-y-2"
      >
        {services.map((s) => {
          const active = selectable && s.id === selectedId;
          const body = (
            <>
              {selectable && (
                <span
                  aria-hidden
                  className={`grid size-5 shrink-0 place-items-center rounded-full border-2 transition duration-150 ${
                    active ? "border-accent" : "border-line"
                  }`}
                >
                  {active && <span className="size-2.5 rounded-full bg-accent" />}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className={`block font-semibold ${active ? "text-accent" : "text-ink"}`}>
                  {s.name}
                </span>
                <span className="block text-sm text-muted">{s.durationDisplay}</span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1">
                <span className="font-mono font-semibold text-ink">
                  {s.priceByLocation[location] ?? s.priceDisplay}
                </span>
                {unavailable && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-page px-2 py-0.5 text-[11px] font-semibold text-muted">
                    <CalendarX className="size-3" aria-hidden strokeWidth={2} />
                    No open times
                  </span>
                )}
              </span>
            </>
          );

          return (
            <li key={s.id}>
              {selectable ? (
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onSelect(s.id)}
                  className={`${shape} transition duration-150 ${
                    active
                      ? "border-accent bg-accent/8"
                      : "border-line bg-surface hover:border-accent/50"
                  }`}
                >
                  {body}
                </button>
              ) : (
                // Dimmed rather than hidden: the clinic still offers these,
                // and a list that empties itself reads as "this clinic does
                // nothing".
                <div
                  aria-disabled={unavailable || undefined}
                  className={`${shape} border-line bg-surface ${
                    unavailable ? "opacity-60 grayscale" : ""
                  }`}
                >
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

/**
 * Where the appointment happens, as one card per way it is offered.
 *
 * Each card carries the price of having it **that** way, already totalled. The
 * previous version showed one price and a "home visits add $15" note underneath,
 * which asked the reader to do the arithmetic and made the home visit look like
 * a penalty rather than a service with its own price. A business that only
 * visits homes had no price at all until you noticed the note.
 */
function WhereToHaveIt({
  offered,
  selected,
  onChange,
  service,
  mapHref,
}: {
  offered: ServiceLocation[];
  selected: ServiceLocation;
  onChange: (next: ServiceLocation) => void;
  service: BookServiceView;
  mapHref?: string;
}) {
  const choosable = offered.length > 1;
  const blurb = choosable
    ? "You can be seen at the clinic, or they can come to you"
    : offered[0] === "at_home"
      ? "This service is a home visit only"
      : "This service is in clinic only";

  return (
    <Panel>
      <h3 className="mb-1 font-bold text-ink">Where</h3>
      <p className="mb-3 text-sm text-muted">{blurb}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        {offered.map((location) => {
          const active = selected === location;
          const Icon = location === "at_home" ? Home : MapPin;
          return (
            <button
              key={location}
              type="button"
              onClick={() => onChange(location)}
              aria-pressed={choosable ? active : undefined}
              aria-label={`${serviceLocationLabel(location)}, ${
                service.priceByLocation[location] ?? service.priceDisplay
              }`}
              // items-stretch because a <button> centres its content, which
              // put the two cards' contents on different lines.
              className={`flex flex-col items-stretch rounded-xl border p-4 text-start transition duration-150 ${
                active
                  ? "border-accent bg-accent/8"
                  : "border-line bg-surface hover:border-accent/50"
              } ${choosable ? "" : "cursor-default"}`}
            >
              <span
                className={`flex items-center justify-between gap-2 font-semibold ${
                  active ? "text-accent" : "text-ink"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="size-4.5 shrink-0" aria-hidden strokeWidth={1.8} />
                  {serviceLocationLabel(location)}
                </span>
                <span className="font-mono">
                  {service.priceByLocation[location] ?? service.priceDisplay}
                </span>
              </span>

              {location === "in_clinic" && mapHref && (
                // No street address is stored, only coordinates, so the link
                // carries the meaning on its own. A span rather than an anchor:
                // this card is already a button.
                <CardMapLink href={mapHref} className="mt-2 text-sm" />
              )}

              {location === "at_home" && (
                <p className="mt-2 text-sm text-muted">
                  The practitioner comes to the address you give below
                </p>
              )}
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

/**
 * Pick a day, then a time.
 *
 * Days first, as cards that say how many times are free, so the choice of day
 * is made on information rather than by scrolling to it. Then that day only,
 * split into morning, afternoon and evening.
 */
function PickATime({
  days,
  picked,
  onPick,
  readOnly,
}: {
  days: BookDayView[];
  picked: { date: string; startMinutes: number } | null;
  onPick: (next: { date: string; startMinutes: number }) => void;
  readOnly: boolean;
}) {
  const [openDate, setOpenDate] = useState(picked?.date ?? days[0]?.date ?? null);
  const [showAll, setShowAll] = useState(false);

  const shown = showAll ? days : days.slice(0, DAYS_SHOWN);
  // The chosen day may fall outside the current availability after a location
  // change, so fall back rather than showing an empty panel.
  const day = days.find((d) => d.date === openDate) ?? days[0] ?? null;

  return (
    <Panel>
      <h3 className="mb-1 font-bold text-ink">
        {readOnly ? "Open times" : "Pick a time"}
      </h3>
      <p className="mb-3 text-sm text-muted">
        Open times only. The business confirms before it is booked
      </p>

      {/* Days. A wrapping grid, never a sideways scroll. */}
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {shown.map((d) => {
          const active = day?.date === d.date;
          return (
            <li key={d.date}>
              <button
                type="button"
                onClick={() => setOpenDate(d.date)}
                aria-pressed={active}
                className={`flex w-full flex-col items-center gap-0.5 rounded-xl border px-1 py-2.5 transition duration-150 ${
                  active
                    ? "border-primary bg-primary text-on-primary"
                    : "border-line bg-surface hover:border-accent/50"
                }`}
              >
                <span
                  className={`text-[11px] font-semibold uppercase tracking-wide ${
                    active ? "text-on-primary/70" : "text-muted"
                  }`}
                >
                  {d.weekdayLabel}
                </span>
                <span
                  className={`font-mono text-lg font-bold leading-none ${
                    active ? "text-on-primary" : "text-ink"
                  }`}
                >
                  {d.dayOfMonth}
                </span>
                <span
                  className={`text-[11px] ${active ? "text-on-primary/70" : "text-muted"}`}
                >
                  {d.blocks.length} free
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {!showAll && days.length > DAYS_SHOWN && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 text-sm font-semibold text-accent hover:underline"
        >
          Show more days
        </button>
      )}

      {day && (
        <div className="mt-5 space-y-4 border-t border-line pt-5">
          <p className="text-sm font-semibold text-ink">{day.dateLabel}</p>
          {PARTS.map((part) => {
            const blocks = day.blocks.filter(
              (b) => partOfDay(b.startMinutes) === part,
            );
            if (blocks.length === 0) return null;
            return (
              <div key={part}>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">
                  {part}
                </p>
                <div className="flex flex-wrap gap-2">
                  {blocks.map((block) => {
                    const active =
                      picked?.date === day.date &&
                      picked?.startMinutes === block.startMinutes;
                    const label = block.startLabel;
                    return readOnly ? (
                      <span
                        key={block.startMinutes}
                        className="rounded-lg border border-line bg-page px-4 py-2 font-mono text-sm text-body"
                      >
                        {label}
                      </span>
                    ) : (
                      <button
                        key={block.startMinutes}
                        type="button"
                        onClick={() =>
                          onPick({ date: day.date, startMinutes: block.startMinutes })
                        }
                        aria-pressed={active}
                        className={`rounded-lg border px-4 py-2 font-mono text-sm font-semibold transition duration-150 ${
                          active
                            ? "border-accent bg-accent text-on-accent"
                            : "border-line bg-surface text-body hover:border-accent/50"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/**
 * "Request sent", as a popup over the page.
 *
 * It used to be a banner at the top of the booking section, which on a phone
 * is a screen above the button that was just pressed, so the answer arrived
 * somewhere nobody was looking. A popup lands where they are. A native
 * `<dialog>` opened with `showModal()` sits in the top layer, so the
 * `backdrop-filter` containing block (design system §12) cannot catch it, and
 * it brings the focus trap and Escape-to-close with it.
 *
 * Refusals stay inline: the recovery from one is on this screen.
 */
function RequestSentDialog({
  bookingsHref,
  onNavigate,
  onClose,
}: {
  bookingsHref: string;
  onNavigate?: (href: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby="request-sent-title"
      aria-describedby="request-sent-detail"
      className="m-auto w-[min(24rem,calc(100vw-2rem))] rounded-2xl border border-line bg-surface p-6 text-center text-body shadow-[var(--shadow-lift)] backdrop:bg-primary/40"
    >
      <CircleCheck
        className="mx-auto mb-3 size-10 text-accent"
        aria-hidden
        strokeWidth={1.8}
      />
      <h2 id="request-sent-title" className="text-lg font-bold text-ink">
        Request sent
      </h2>
      <p id="request-sent-detail" className="mt-2 text-sm text-muted">
        You&apos;ll be notified when the business confirms it.
      </p>
      <div className="mt-6 flex flex-col gap-2">
        <AppLink
          href={bookingsHref}
          onNavigate={onNavigate}
          className={buttonClass("primary", "w-full")}
        >
          Your bookings
        </AppLink>
        <button
          type="button"
          onClick={() => ref.current?.close()}
          className={buttonClass("ghost", "w-full")}
        >
          Done
        </button>
      </div>
    </dialog>
  );
}

/**
 * The one control that sends the request.
 *
 * It is never disabled. A disabled button is a dead end: it does not say what
 * is missing, and on a touch screen it does not even acknowledge the tap. This
 * one always answers, and when something is missing it says which thing.
 */
function SendRequest({
  ready,
  pending,
  onBlocked,
}: {
  ready: boolean;
  /** Supplied by the caller: there is no form action to read it from. */
  pending: boolean;
  onBlocked: () => void;
}) {
  return (
    <button
      type={ready ? "submit" : "button"}
      onClick={ready ? undefined : onBlocked}
      disabled={pending}
      aria-disabled={!ready}
      className={`mt-4 w-full rounded-lg px-5 py-3 font-semibold transition ${
        ready
          ? "bg-primary text-on-primary hover:brightness-110"
          : "bg-primary/40 text-on-primary"
      } disabled:opacity-60`}
    >
      {pending ? "Sending…" : "Send request"}
    </button>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      {children}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="text-end font-medium text-ink">{value}</dd>
    </div>
  );
}
